import {
  forwardRef,
  useCallback,
  useMemo,
  useState,
  useRef,
  useEffect,
} from "react";
import {
  MaskedTimeInput,
  MaskedTimeInputChange,
} from "./internal/MaskedTimeInput";
import { TimeFieldProps } from "./internal/types";
import { TIME_FORMAT_PLACEHOLDERS } from "./internal/constants";
import { useOptionallyControlledState, useMergeRefs } from "../../hooks";
import {
  generateTimeOptions,
  filterTimeOptions,
} from "./internal/utils/timeOptions";
import {
  handleAutoRounding,
  getAutoRoundingOptions,
} from "./internal/utils/timeRounding";
import { Popover } from "../Popover";
import { Listbox } from "../Listbox";
import {
  usePopoverSupport,
  usePopoverCloseDelayWorkaround,
} from "../../internal/hooks";

/**
 * TimeField component for entering time values with auto-rounding and suggestions.
 *
 * Features:
 * - Supports 12-hour and 24-hour time formats
 * - Min/max time constraints handled during input parsing
 * - Auto-rounding to step intervals when autoround is true
 * - Dropdown suggestions for quick time selection
 * - Controlled and uncontrolled modes
 * - Smart input masking with placeholders
 * - Integration with MaskedTimeInput for auto-complete
 * - Validation handled by implementors using onChange callback
 *
 * @example
 * <TimeField
 *   label="Meeting Time"
 *   value="14:30"
 *   format={24}
 *   step={30}
 *   min="09:00"
 *   max="17:00"
 *   autoround
 *   disableSuggestions
 *   onChange={(change) => {
 *     // Handle time change and validation
 *     if (change.isInputEmpty && required) {
 *       // Show required error
 *     }
 *   }}
 * />
 */
export const TimeFieldElement = forwardRef<HTMLInputElement, TimeFieldProps>(
  function TimeField(
    {
      value: valueProp,
      defaultValue,
      min,
      max,
      onChange,
      format = 12,
      step = 30,
      required,
      description,
      error: errorProp,
      disableSuggestions = false,
      autoround = false,
      className,
      ...rest
    },
    ref,
  ) {
    // Use optionally controlled state for last valid time management (following DateField pattern)
    const [lastValidTime, setLastValidTime] = useOptionallyControlledState<
      string | null
    >({
      controlledValue: valueProp,
      defaultValue: defaultValue ?? null,
    });

    // Dropdown state management
    const [isDropdownOpen, setIsDropdownOpenRaw] = useState(false);

    const setIsDropdownOpen = (open: boolean) => {
      setIsDropdownOpenRaw(open);
    };
    const [selectedOptionIndex, setSelectedOptionIndex] = useState(0);
    const [inputValueForFiltering, setInputValueForFiltering] = useState("");
    const [currentParsedTime, setCurrentParsedTime] = useState<string | null>(
      null,
    );

    // Get parsed data from MaskedTimeInput via data attributes
    const [inputParsedData, setInputParsedData] = useState<{
      time: string | null;
      isInputValid: boolean;
      isInputEmpty: boolean;
    }>({ time: null, isInputValid: false, isInputEmpty: true });

    // Track the last onChange call to prevent duplicate calls
    const lastOnChangeCallRef = useRef<{
      time: string | null;
      isInputValid: boolean;
      isInputEmpty: boolean;
    } | null>(null);

    // Track if dropdown is being clicked (vs tabbed to)
    const isDropdownClickingRef = useRef(false);

    // Track if we just made a selection (to prevent auto-reopening on focus)
    const [justSelected, setJustSelected] = useState(false);

    // Track if Tab was just pressed (to prevent useEffect from reopening dropdown)
    const tabJustPressedRef = useRef(false);

    // Popover support and refs
    const inputRef = useRef<
      HTMLInputElement & {
        resetTypingFlag?: () => void;
      }
    >(null);
    const popoverContentRef = useRef<HTMLDivElement>(null);
    const combinedRef = useMergeRefs([inputRef, ref]);
    const popoverSupported = usePopoverSupport();

    // Get placeholder based on format
    const placeholder = TIME_FORMAT_PLACEHOLDERS[format];

    // Optimize dropdown rendering - only show options when dropdown is open
    const shouldShowOptionsFromHook =
      usePopoverCloseDelayWorkaround(isDropdownOpen);

    // Override the delay when Tab was just pressed to prevent empty popover
    const shouldShowOptions = tabJustPressedRef.current
      ? false
      : shouldShowOptionsFromHook;

    // Cleanup timeouts on unmount
    const dropdownTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Cleanup timeouts when component unmounts
    useEffect(() => {
      const dropdownRef = dropdownTimeoutRef.current;
      return () => {
        if (dropdownRef) {
          clearTimeout(dropdownRef);
        }
      };
    }, []);

    // Generate time options for dropdown
    const allTimeOptions = useMemo(
      () => generateTimeOptions({ step, min, max, format }),
      [step, min, max, format],
    );

    // Split the complex filtering logic into focused memos
    const baseFilteredOptions = useMemo(() => {
      // If no input or placeholder, show all options
      if (!inputValueForFiltering || inputValueForFiltering.match(/^[–_:]+$/)) {
        return allTimeOptions;
      }

      return filterTimeOptions(allTimeOptions, inputValueForFiltering);
    }, [allTimeOptions, inputValueForFiltering]);

    const autoRoundingOptions = useMemo(() => {
      // Only generate auto-rounding options if enabled and no base matches
      if (
        !autoround ||
        baseFilteredOptions.length > 0 ||
        !inputValueForFiltering ||
        inputValueForFiltering.match(/^[–_:]+$/)
      ) {
        return [];
      }

      // Get current time for auto-rounding logic
      const currentTime = currentParsedTime || inputParsedData.time;

      const autoRoundedOptions = getAutoRoundingOptions(
        currentTime,
        step ?? 30,
        format,
        inputValueForFiltering,
        min,
      );

      // Filter out any options that are already in baseFiltered
      return autoRoundedOptions.filter(
        (option) => !baseFilteredOptions.includes(option),
      );
    }, [
      autoround,
      baseFilteredOptions,
      inputValueForFiltering,
      currentParsedTime,
      inputParsedData.time,
      format,
      min,
      step,
    ]);

    const filteredOptions = useMemo(() => {
      // If input is completely invalid AND we have no matches, show all options
      if (
        baseFilteredOptions.length === 0 &&
        autoRoundingOptions.length === 0
      ) {
        if (
          !inputParsedData.isInputValid &&
          inputValueForFiltering &&
          !inputValueForFiltering.match(/^[–_:]+$/)
        ) {
          return allTimeOptions;
        }
      }

      return [...baseFilteredOptions, ...autoRoundingOptions];
    }, [
      baseFilteredOptions,
      autoRoundingOptions,
      allTimeOptions,
      inputValueForFiltering,
      inputParsedData.isInputValid,
    ]);

    // Handle blur - close dropdown and validate current input state
    const handleBlur = useCallback(
      (event: React.FocusEvent<HTMLInputElement>) => {
        // Skip blur processing if clicking in the dropdown (prevents duplicate onChange)
        // but allow blur for other cases like tabbing away
        if (
          event.relatedTarget &&
          popoverContentRef.current?.contains(event.relatedTarget as Node) &&
          isDropdownClickingRef.current
        ) {
          isDropdownClickingRef.current = false; // Reset flag
          return;
        }

        // Close dropdown on blur
        setIsDropdownOpen(false);
        setSelectedOptionIndex(0);

        // Get parsed data from MaskedTimeInput data attributes - following DateFields pattern
        const input = event.target as HTMLInputElement;
        const parsedInputTime = input.dataset.time || null;
        const isInputValid = input.dataset.inputValid === "true";
        const isInputEmpty = input.dataset.inputEmpty === "true";

        // Use the parsed input time directly, don't fall back to old values
        let finalTime = parsedInputTime;

        // Auto-rounding logic - apply when autoround is enabled regardless of suggestions
        if (autoround && finalTime && isInputValid) {
          finalTime = handleAutoRounding(finalTime, {
            autoround,
            step,
            format,
          });
        }

        // Set lastValidTime to trigger input value update
        if (finalTime) {
          // Reset typing flag so MaskedTimeInput will accept the new value
          inputRef.current?.resetTypingFlag?.();
          setLastValidTime(finalTime);
        }

        // Check if this onChange call would be a duplicate
        const currentChange = {
          time: finalTime,
          isInputValid,
          isInputEmpty,
        };

        const lastChange = lastOnChangeCallRef.current;
        const isDuplicate =
          lastChange &&
          lastChange.time === currentChange.time &&
          lastChange.isInputValid === currentChange.isInputValid &&
          lastChange.isInputEmpty === currentChange.isInputEmpty;

        // Only call onChange if this is not a duplicate
        if (!isDuplicate) {
          onChange?.(currentChange);
          lastOnChangeCallRef.current = currentChange;
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [format, step, autoround, onChange],
    );

    // Add focus handler
    const handleFocus = useCallback(() => {
      if (!disableSuggestions && !justSelected) {
        setIsDropdownOpen(true);
        // Don't automatically select the first option on focus
        // This allows the input to remain empty when user just focuses
        setSelectedOptionIndex(0);
      }
    }, [disableSuggestions, justSelected]);

    // Handle dropdown state based on filtered options
    useEffect(() => {
      // Close dropdown if no options available or only 1 option (no choice to make)
      if (filteredOptions.length <= 1 && isDropdownOpen) {
        setIsDropdownOpen(false);
      }

      // Reopen dropdown when multiple options become available while input is focused
      if (
        !disableSuggestions &&
        filteredOptions.length > 1 &&
        document.activeElement &&
        document.activeElement.tagName === "INPUT" &&
        !isDropdownOpen &&
        !justSelected &&
        !tabJustPressedRef.current
      ) {
        setIsDropdownOpen(true);
        setSelectedOptionIndex(0);
      }
    }, [filteredOptions, disableSuggestions, isDropdownOpen, justSelected]);

    // Handle input changes from MaskedTimeInput
    const handleInputChange = (change: MaskedTimeInputChange) => {
      // Update input value for filtering
      setInputValueForFiltering(change.event.target.value);

      // Update current parsed time for auto-rounding logic
      setCurrentParsedTime(change.time);

      // Update parsed data state from the change event
      setInputParsedData({
        time: change.time,
        isInputValid: change.isInputValid,
        isInputEmpty: change.isInputEmpty,
      });

      // Reset selected option index when user types
      setSelectedOptionIndex(0);

      // Clear the just-selected flag when user starts typing
      setJustSelected(false);
    };

    // Scroll the selected option into view within the popover
    const scrollToSelectedOption = useCallback(() => {
      if (!popoverContentRef.current || selectedOptionIndex < 0) return;

      // Get all option elements and select by index
      const optionElements =
        popoverContentRef.current.querySelectorAll('[role="option"]');
      const targetElement = optionElements[selectedOptionIndex];

      if (targetElement) {
        targetElement.scrollIntoView({
          block: "center",
          behavior: "auto",
        });
      }
    }, [selectedOptionIndex]);

    // Scroll to selected option when selectedOptionIndex changes
    useEffect(() => {
      if (isDropdownOpen && selectedOptionIndex >= 0) {
        scrollToSelectedOption();
      }
    }, [selectedOptionIndex, isDropdownOpen, scrollToSelectedOption]);

    // Handle dropdown option selection (Enter key and mouse clicks)
    const handleOptionSelect = useCallback(
      (selectedTime: string) => {
        // Reset typing flag FIRST to ensure MaskedTimeInput will accept the new value
        if (inputRef.current?.resetTypingFlag) {
          inputRef.current.resetTypingFlag();
        }

        // Close dropdown after selection
        setIsDropdownOpen(false);
        setSelectedOptionIndex(0);

        // Set flag to prevent auto-reopening on focus
        setJustSelected(true);

        // Update all state to reflect the selection
        setLastValidTime(selectedTime);
        setCurrentParsedTime(selectedTime);

        const changeData = {
          time: selectedTime,
          isInputValid: true,
          isInputEmpty: false,
        };

        // Notify parent of the change
        onChange?.(changeData);

        // Update the last onChange call ref to prevent duplicate in blur
        lastOnChangeCallRef.current = changeData;

        // Focus the input after selection
        requestAnimationFrame(() => {
          inputRef.current?.focus();
        });
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [onChange],
    );

    // Handle keyboard navigation
    const handleKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (!isDropdownOpen) {
          return; // Let normal input behavior handle it
        }

        switch (event.key) {
          case "ArrowDown":
            event.preventDefault();
            setSelectedOptionIndex((prev) =>
              prev < filteredOptions.length - 1 ? prev + 1 : 0,
            );
            break;
          case "ArrowUp":
            event.preventDefault();
            setSelectedOptionIndex((prev) =>
              prev > 0 ? prev - 1 : filteredOptions.length - 1,
            );
            break;
          case "Enter":
            event.preventDefault();
            if (filteredOptions[selectedOptionIndex]) {
              // Enter: select option, keep dropdown open
              handleOptionSelect(filteredOptions[selectedOptionIndex]);
            }
            break;
          case "Tab":
            // Set flag to prevent useEffect from reopening dropdown
            tabJustPressedRef.current = true;

            // Close dropdown and let natural tab behavior happen
            setIsDropdownOpen(false);
            setSelectedOptionIndex(0);

            // Clear the flag after blur completes
            setTimeout(() => {
              tabJustPressedRef.current = false;
            }, 50);
            break;
        }
      },
      [
        isDropdownOpen,
        selectedOptionIndex,
        filteredOptions,
        handleOptionSelect,
      ],
    );

    const popoverDisabled = disableSuggestions || !popoverSupported;

    const justTheInput = (
      <MaskedTimeInput
        ref={combinedRef}
        lastValidTime={lastValidTime}
        format={format}
        placeholder={placeholder}
        min={min}
        max={max}
        onInputChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        onFocus={handleFocus}
        description={description}
        error={errorProp}
        className={popoverDisabled ? className : undefined}
        {...rest}
        data-anv="time-field"
      />
    );

    if (popoverDisabled) {
      return justTheInput;
    }

    return (
      <Popover
        open={isDropdownOpen}
        noPadding
        disableCaret
        placement="bottom-start"
        matchReferenceWidth
        modal={false}
        onClickOutside={() => {
          setIsDropdownOpen(false);
        }}
        onClose={() => {
          setIsDropdownOpen(false);
        }}
        fitScreen
      >
        <Popover.Trigger>
          {({ ref: popoverRef }) => (
            <div
              ref={popoverRef as unknown as React.Ref<HTMLDivElement>}
              className={className}
            >
              {justTheInput}
            </div>
          )}
        </Popover.Trigger>

        <Popover.Content
          ref={popoverContentRef}
          data-open={isDropdownOpen}
          data-disabled={disableSuggestions}
          onMouseDown={() => {
            // Set flag to indicate dropdown is being clicked (desktop)
            isDropdownClickingRef.current = true;
          }}
          onTouchStart={() => {
            // Set flag to indicate dropdown is being touched (mobile)
            isDropdownClickingRef.current = true;
          }}
        >
          {shouldShowOptions && (
            <Listbox
              selected={filteredOptions[selectedOptionIndex]}
              onSelectionChange={(selected: string | undefined) => {
                if (selected) {
                  handleOptionSelect(selected);
                } else {
                  // Re-select the same option instead of deselecting (for TimeField, we never want deselection)
                  const currentOption = filteredOptions[selectedOptionIndex];
                  if (currentOption) {
                    handleOptionSelect(currentOption);
                  }
                }
              }}
              aria-label="Time suggestions"
            >
              {filteredOptions.map((option) => (
                <Listbox.Option key={option} value={option} label={option}>
                  {option}
                </Listbox.Option>
              ))}
            </Listbox>
          )}
        </Popover.Content>
      </Popover>
    );
  },
);

export const TimeField = Object.assign(TimeFieldElement, {});

TimeField.displayName = "TimeField";
